/**
 * CL-FILE-CLASSIFICATION-001 — Upload-time project file classification (pure).
 *
 * Explicit admin selection always wins. PDF alone is not a drawing.
 * Strong sheet identifiers and drawing terms classify as drawing.
 */

import { PROJECT_FILE_DOMAINS } from "./projectFileDomainModel";
import { suggestDisplayTitleFromFilename } from "./projectFileDisplayTitleModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

const DRAWING_SHEET_PREFIX_RE = /\b[ASECMPL][-.]?\d{1,4}(?:[.-]\d{1,2})?[A-Z]?\b/i;

// Compound phrases first (higher confidence).
const DRAWING_PHRASES = [
  "floor plan",
  "site plan",
  "roof plan",
  "roof framing",
  "reflected ceiling",
  "ceiling plan",
  "demo plan",
  "demolition plan",
  "permit set",
  "as-built",
  "as built",
  "foundation plan",
  "framing plan",
  "structural plan",
];

// Word-boundary terms (case-insensitive).
const DRAWING_TERMS = [
  "framing",
  "section",
  "elevation",
  "foundation",
  "demolition",
  "structural",
  "detail",
  "plan", // checked carefully — see matchDrawingTerm
];

const FALSE_POSITIVE_PLAN_CONTEXT = [
  "planning",
  "airplane",
  "planter",
  "plant",
  "explanation",
  "implementation",
  "business plan",
  "marketing plan",
  "action plan",
  "project plan",
  "work plan",
];

function isImageContentType(type) {
  return lower(type).startsWith("image/");
}

function isImageName(name) {
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(safeStr(name));
}

function isPdfName(name) {
  return lower(name).endsWith(".pdf");
}

function isModelName(name) {
  return /\.(skp|rvt|ifc|dwg|dxf)$/i.test(safeStr(name));
}

function normalizeSelectedLibraryType(selected) {
  const s = lower(selected);
  if (!s || s === "auto") return "";
  if (s === "form" || s === "report") return "document";
  if (["drawing", "document", "photo", "model", "client_document", "controlled_document"].includes(s)) {
    return s;
  }
  return "";
}

function domainForLibraryType(libraryType) {
  const lt = lower(libraryType);
  if (lt === "drawing") return PROJECT_FILE_DOMAINS.DRAWING_REVIEW;
  if (lt === "photo") return PROJECT_FILE_DOMAINS.PROJECT_PHOTO;
  if (lt === "client_document") return PROJECT_FILE_DOMAINS.CLIENT_SUPPORTING_DOCUMENT;
  if (lt === "controlled_document") return PROJECT_FILE_DOMAINS.CONTROLLED_DOCUMENT;
  return PROJECT_FILE_DOMAINS.GENERAL_PROJECT_FILE;
}

function nameHasFalsePositivePlanContext(nameLower) {
  return FALSE_POSITIVE_PLAN_CONTEXT.some((ctx) => nameLower.includes(ctx));
}

function matchDrawingPhrase(nameLower) {
  for (const phrase of DRAWING_PHRASES) {
    if (nameLower.includes(phrase)) {
      return { matched: true, rule: `phrase:${phrase}`, confidence: "high" };
    }
  }
  return { matched: false };
}

function matchDrawingTerm(nameLower) {
  for (const term of DRAWING_TERMS) {
    if (term === "plan") {
      if (nameHasFalsePositivePlanContext(nameLower)) continue;
      // Require plan as a token, not inside longer prose words already filtered.
      if (/\bplans?\b/.test(nameLower) && !/\b(business|marketing|action|project|work)\s+plans?\b/.test(nameLower)) {
        // Bare "plan" alone on a PDF is medium — sheet prefix or other terms raise confidence elsewhere.
        return { matched: true, rule: "term:plan", confidence: "medium" };
      }
      continue;
    }
    const re = new RegExp(`\\b${term}\\b`, "i");
    if (re.test(nameLower)) {
      return { matched: true, rule: `term:${term}`, confidence: "high" };
    }
  }
  return { matched: false };
}

function matchDrawingSheetPrefix(fileName) {
  const name = safeStr(fileName);
  // Prefer architectural/structural style: A-404B, S-101, A404, etc.
  const match = name.match(/\b([ASECMPL])[-.]?(\d{1,4})(?:[.-]\d{1,2})?([A-Z])?\b/i);
  if (match) {
    return {
      matched: true,
      rule: `sheet_prefix:${match[1].toUpperCase()}-${match[2]}${match[3] || ""}`,
      confidence: "high",
      sheetHint: match[0],
    };
  }
  if (DRAWING_SHEET_PREFIX_RE.test(name)) {
    return { matched: true, rule: "sheet_prefix:generic", confidence: "medium" };
  }
  return { matched: false };
}

/**
 * Pure upload classification.
 */
export function classifyProjectFileUpload({
  fileName = "",
  mimeType = "",
  selectedLibraryType = "auto",
  uploadContext = "",
  metadata = null,
} = {}) {
  const name = safeStr(fileName);
  const nameLower = lower(name);
  const mime = lower(mimeType || metadata?.contentType);
  const context = lower(uploadContext || metadata?.category || metadata?.subcategory || "");
  const explicit = normalizeSelectedLibraryType(
    selectedLibraryType || metadata?.manualLibraryType || metadata?.libraryTypeManual
  );

  if (explicit) {
    return {
      libraryType: explicit,
      projectFileDomain: domainForLibraryType(explicit),
      confidence: "high",
      matchedRule: `explicit:${explicit}`,
      requiresConfirmation: false,
    };
  }

  if (isImageContentType(mime) || isImageName(name) || context.includes("photo")) {
    return {
      libraryType: "photo",
      projectFileDomain: PROJECT_FILE_DOMAINS.PROJECT_PHOTO,
      confidence: "high",
      matchedRule: "image",
      requiresConfirmation: false,
    };
  }

  if (isModelName(name) || mime.includes("model") || context.includes("model") || context.includes("3d")) {
    return {
      libraryType: "model",
      projectFileDomain: PROJECT_FILE_DOMAINS.PROJECT_DOCUMENT,
      confidence: "high",
      matchedRule: "model_extension",
      requiresConfirmation: false,
    };
  }

  if (context.includes("drawing")) {
    return {
      libraryType: "drawing",
      projectFileDomain: PROJECT_FILE_DOMAINS.DRAWING,
      confidence: "high",
      matchedRule: "upload_context:drawing",
      requiresConfirmation: false,
    };
  }

  const phrase = matchDrawingPhrase(nameLower);
  if (phrase.matched) {
    return {
      libraryType: "drawing",
      projectFileDomain: PROJECT_FILE_DOMAINS.DRAWING,
      confidence: phrase.confidence,
      matchedRule: phrase.rule,
      requiresConfirmation: phrase.confidence !== "high",
    };
  }

  const sheet = matchDrawingSheetPrefix(name);
  if (sheet.matched) {
    return {
      libraryType: "drawing",
      projectFileDomain: PROJECT_FILE_DOMAINS.DRAWING,
      confidence: sheet.confidence,
      matchedRule: sheet.rule,
      requiresConfirmation: sheet.confidence !== "high",
    };
  }

  const term = matchDrawingTerm(nameLower);
  if (term.matched) {
    return {
      libraryType: "drawing",
      projectFileDomain: PROJECT_FILE_DOMAINS.DRAWING,
      confidence: term.confidence,
      matchedRule: term.rule,
      requiresConfirmation: term.confidence !== "high",
    };
  }

  // PDF alone → document (not drawing). Low-confidence default.
  if (isPdfName(name) || mime.includes("pdf")) {
    return {
      libraryType: "document",
      projectFileDomain: PROJECT_FILE_DOMAINS.PROJECT_DOCUMENT,
      confidence: "low",
      matchedRule: "fallback:pdf_document",
      requiresConfirmation: true,
    };
  }

  return {
    libraryType: "document",
    projectFileDomain: PROJECT_FILE_DOMAINS.PROJECT_DOCUMENT,
    confidence: "low",
    matchedRule: "fallback:document",
    requiresConfirmation: true,
  };
}

/**
 * Normalize filename for duplicate comparison (case-insensitive, trimmed).
 */
export function normalizeProjectFileNameForDuplicateCheck(fileName = "") {
  return lower(fileName).replace(/\s+/g, " ");
}

/**
 * Detect likely duplicate uploads without auto-linking revisions.
 */
export function findLikelyDuplicateProjectFiles({
  fileName = "",
  fileSize = null,
  projectFiles = [],
  projectId = "",
  uploadTimeMs = Date.now(),
  proximityMs = 7 * 24 * 60 * 60 * 1000,
} = {}) {
  const targetName = normalizeProjectFileNameForDuplicateCheck(fileName);
  if (!targetName) {
    return { isDuplicate: false, matches: [], warning: "", allowUploadAnyway: true, looksLikeRevision: false };
  }

  const pid = safeStr(projectId);
  const matches = (Array.isArray(projectFiles) ? projectFiles : []).filter((f) => {
    if (safeStr(f?.status).toLowerCase() === "deleted") return false;
    if (pid && safeStr(f?.projectId) && safeStr(f.projectId) !== pid) return false;
    const existingName = normalizeProjectFileNameForDuplicateCheck(f?.name || f?.fileName);
    if (existingName !== targetName) return false;
    return true;
  });

  if (!matches.length) {
    return { isDuplicate: false, matches: [], warning: "", allowUploadAnyway: true, looksLikeRevision: false };
  }

  const sizeMatches = matches.filter((f) => {
    if (typeof fileSize !== "number" || typeof f?.size !== "number") return false;
    return f.size === fileSize;
  });

  const looksLikeRevision =
    /\b(rev|revision|ver|version|v\d+|r\d+)\b/i.test(fileName) ||
    matches.some((f) => safeStr(f?.revision) && safeStr(f.revision) !== "v1");

  const samePath = matches.some(
    (f) => safeStr(f?.path) && safeStr(f.path).toLowerCase().includes(targetName.replace(/\s/g, ""))
  );

  const recent = matches.some((f) => {
    const created = f?.createdAt?.toMillis?.() || f?.createdAt?.seconds * 1000 || 0;
    if (!created) return true;
    return Math.abs(uploadTimeMs - created) <= proximityMs;
  });

  const strong =
    sizeMatches.length > 0 || samePath || (matches.length > 0 && recent && !looksLikeRevision);

  return {
    isDuplicate: true,
    matches,
    sizeMatched: sizeMatches.length > 0,
    warning: looksLikeRevision
      ? "A file with this name already exists. If this is a new revision, upload anyway — revision linking is manual."
      : "A file with this name already exists in this project.",
    allowUploadAnyway: true,
    looksLikeRevision,
    strongDuplicateSignal: strong,
  };
}

/**
 * Build per-file upload review rows (no File objects serialized).
 */
export function buildUploadClassificationReviewRows({
  files = [],
  selectedLibraryType = "auto",
  uploadContext = "",
  projectFiles = [],
  projectId = "",
} = {}) {
  return (Array.isArray(files) ? files : []).map((file, index) => {
    const fileName = safeStr(file?.name);
    const mimeType = safeStr(file?.type);
    const classification = classifyProjectFileUpload({
      fileName,
      mimeType,
      selectedLibraryType,
      uploadContext,
    });
    const duplicate = findLikelyDuplicateProjectFiles({
      fileName,
      fileSize: typeof file?.size === "number" ? file.size : null,
      projectFiles,
      projectId,
    });
    return {
      index,
      fileName,
      displayTitle: suggestDisplayTitleFromFilename(fileName),
      mimeType,
      size: typeof file?.size === "number" ? file.size : null,
      detectedLibraryType: classification.libraryType,
      selectedLibraryType: classification.libraryType,
      confidence: classification.confidence,
      matchedRule: classification.matchedRule,
      requiresConfirmation: classification.requiresConfirmation || duplicate.isDuplicate,
      projectFileDomain: classification.projectFileDomain,
      duplicateWarning: duplicate.isDuplicate ? duplicate.warning : "",
      duplicateLooksLikeRevision: duplicate.looksLikeRevision,
      duplicateMatchIds: duplicate.matches.map((m) => safeStr(m.id)).filter(Boolean),
    };
  });
}

/**
 * Documents / Drawings list membership after reclassification (pure).
 */
export function projectFileListMembershipAfterLibraryType(libraryType = "") {
  const lt = lower(libraryType) || "document";
  return {
    inDrawings: lt === "drawing",
    inDocuments: ["document", "model"].includes(lt),
    inPhotos: lt === "photo",
    engineerPackageEligible: lt === "drawing",
  };
}
